import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, ShoppingBag, Users, Clock, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
      // Play sound for new orders
      playNotificationSound();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/orders`)
      ]);
      
      const prevOrdersCount = recentOrders.length;
      const newOrders = ordersRes.data.slice(0, 10);
      
      // Check if there are new orders
      if (prevOrdersCount > 0 && newOrders.length > prevOrdersCount) {
        toast.success('🔔 Nouvelle commande reçue !', {
          duration: 10000
        });
        playNotificationSound();
      }
      
      setStats(statsRes.data);
      setRecentOrders(newOrders);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVbHm77BaGAg+ltryxnMnBSyAzvLaiTYIGWe77eeeTRAMUKXi8LZjHAY4kNfyy3ksBSR2xvDdkEALFF604+u');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Payée' },
      preparing: { color: 'bg-blue-100 text-blue-800', label: 'En préparation' },
      ready: { color: 'bg-purple-100 text-purple-800', label: 'Prête' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Récupérée' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold" data-testid="admin-dashboard-title">Tableau de bord</h1>
          <div className="flex gap-4">
            <Link
              to="/admin/products"
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
            >
              Gérer les produits
            </Link>
            <Link
              to="/admin/orders"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Voir toutes les commandes
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Commandes totales</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="total-orders">{stats?.total_orders || 0}</p>
              </div>
              <ShoppingBag className="text-blue-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">En attente</p>
                <p className="text-3xl font-bold text-yellow-600" data-testid="pending-orders">{stats?.pending_orders || 0}</p>
              </div>
              <Clock className="text-yellow-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Aujourd'hui</p>
                <p className="text-3xl font-bold text-green-600" data-testid="today-orders">{stats?.today_orders_count || 0}</p>
              </div>
              <TrendingUp className="text-green-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Clients</p>
                <p className="text-3xl font-bold text-purple-600" data-testid="total-customers">{stats?.total_customers || 0}</p>
              </div>
              <Users className="text-purple-600" size={40} />
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Dernières commandes</h2>
            <div className="flex items-center gap-2 text-green-600">
              <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold">Actualisation automatique</span>
            </div>
          </div>

          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune commande</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Retrait</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50" data-testid={`dashboard-order-${order.id}`}>
                      <td className="px-4 py-4 text-sm">
                        {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-semibold text-sm">{order.user_name}</p>
                          <p className="text-xs text-gray-500">{order.user_phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {order.pickup_date} - {order.pickup_time}
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-bold text-green-600">{order.total_amount.toFixed(2)}€</span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(order.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;